import { useState, useEffect, useCallback, memo } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { TextField, IconButton, Select, MenuItem, Divider } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Popover from "@mui/material/Popover";
import { useMainContext } from "../../../../../../context/useMainContext";
import { getTableHeaders } from "../../../../hooks/useTableColumnsConfig";
import { updateUrlParams } from "../../../../../../utils/urlParams";
import { statusOptions } from "../../../../DataGridTable/FieldTypes/StatusCell";

// Filter operators that require a value to be valid
export const VALUE_REQUIRED_OPERATORS = new Set([
  "contains",
  "does not contain",
  "equals",
  "does not equal",
  "starts with",
  "ends with",
  "is any of",
  "greater than",
  "less than",
]);

// All available filter operators
export const ALL_FILTER_OPERATORS = [
  "contains",
  "does not contain",
  "equals",
  "does not equal",
  "starts with",
  "ends with",
  "is empty",
  "is not empty",
  "is any of",
  "greater than",
  "less than",
];

const FiltersPopover = memo(function FiltersPopover({ filtersAnchorEl, setFiltersAnchorEl }) {
  const { filters, setFilters, filtersLogic, setFiltersLogic, visibleColumns, setPage, isInitialLoad, saveUndoData } =
    useMainContext();

  const columnHeaders = getTableHeaders({ columns: visibleColumns });
  const filtersOpen = Boolean(filtersAnchorEl);
  const [localFilters, setLocalFilters] = useState([]);
  const [localFiltersLogic, setLocalFiltersLogic] = useState("and");

  useEffect(() => {
    if (filtersOpen) {
      const validFilters = filters.filter((filter) => columnHeaders.some((col) => col.key === filter.column));
      setLocalFilters(validFilters);
      setLocalFiltersLogic(filtersLogic);
    }
  }, [filtersOpen, filters, filtersLogic]);

  const handleClose = useCallback(() => {
    // Filter out filters without values
    const validFilters = localFilters.filter((f) => {
      if (!VALUE_REQUIRED_OPERATORS.has(f.operator)) return true; // operators that don't need a value
      const v = f.value;
      return v != null && String(v).trim() !== "";
    });

    const previousFilters = [...filters];
    const previousFiltersLogic = filtersLogic;

    const saveData = (nextFilters, nextLogic) => {
      setFilters(nextFilters);
      setFiltersLogic(nextLogic);
      setPage(0);
      if (!isInitialLoad) {
        updateUrlParams({ filters: nextFilters, logic: nextLogic, page: 0 });
      }
    };

    // Compare only meaningful keys to avoid false positives from UI-only fields (e.g., id)
    const toComparable = (arr) =>
      arr.map(({ column, operator, value, label, columnLabel }) => ({
        column,
        operator,
        value: value ?? "",
        label: label ?? "",
        columnLabel: columnLabel ?? "",
      }));

    const changed =
      JSON.stringify(toComparable(previousFilters)) !== JSON.stringify(toComparable(validFilters)) ||
      previousFiltersLogic !== localFiltersLogic;

    if (changed) {
      // Persist changes only when something actually changed
      saveData(validFilters, localFiltersLogic);
      saveUndoData({
        action: "Filter(s) changed",
        undoFunction: () => saveData(previousFilters, previousFiltersLogic),
        redoFunction: () => saveData(validFilters, localFiltersLogic),
      });
    }

    setFiltersAnchorEl(null);
  }, [
    localFilters,
    localFiltersLogic,
    isInitialLoad,
    setFilters,
    setFiltersLogic,
    setPage,
    setFiltersAnchorEl,
    filters,
    filtersLogic,
    saveUndoData,
  ]);

  const filterOperators = ALL_FILTER_OPERATORS;

  const handleAddFilter = useCallback(() => {
    const availableColumns = columnHeaders.map((col) => col.key);
    const firstColumn = columnHeaders.find((col) => col.key === availableColumns[0]);
    setLocalFilters((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        column: availableColumns[0] || "",
        operator: "contains",
        value: "",
        label: "",
        columnLabel: firstColumn?.label || "",
      },
    ]);
  }, [columnHeaders]);

  const handleClearAllFilters = useCallback(() => {
    setLocalFilters([]);
  }, []);

  return (
    <Popover
      open={filtersOpen}
      anchorEl={filtersAnchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        },
      }}
    >
      {localFilters.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            overflowY: "auto",
            p: 2,
          }}
        >
          {localFilters.map((filter, index) => (
            <Box
              key={filter.id || `${filter.column}-${filter.operator}-${filter.value || ""}-${index}`}
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Select
                size="small"
                value={filter.column}
                onChange={(e) => {
                  const updated = [...localFilters];
                  updated[index].column = e.target.value;
                  // Update label and columnLabel when column changes
                  const selectedColumn = columnHeaders.find((col) => col.key === e.target.value);
                  updated[index].label = "";
                  updated[index].columnLabel = selectedColumn?.label || "";
                  setLocalFilters(updated);
                }}
                error={filter.column && !columnHeaders.some((col) => col.key === filter.column)}
                sx={{ width: 170 }}
              >
                {columnHeaders.map((col) => (
                  <MenuItem key={col.key} value={col.key}>
                    {col.label}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                value={filter.operator}
                onChange={(e) => {
                  const updated = [...localFilters];
                  updated[index].operator = e.target.value;
                  setLocalFilters(updated);
                }}
                sx={{ width: 170 }}
              >
                {filterOperators.map((op) => (
                  <MenuItem key={op} value={op}>
                    {op}
                  </MenuItem>
                ))}
              </Select>

              {filter.column === "status" ? (
                <Select
                  size="small"
                  value={filter.value || ""}
                  onChange={(e) => {
                    const updated = [...localFilters];
                    // Convert UI status name back to DB name for filtering
                    updated[index].value = e.target.value;
                    // Update label for status - empty if no value
                    if (e.target.value) {
                      const selectedStatus = statusOptions.find((status) => status.value === e.target.value);
                      updated[index].label = selectedStatus?.label || "";
                    } else {
                      updated[index].label = "";
                    }
                    setLocalFilters(updated);
                  }}
                  displayEmpty
                  sx={{ width: 170 }}
                >
                  <MenuItem value="" disabled>
                    All Statuses
                  </MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <TextField
                  size="small"
                  value={filter.value}
                  placeholder="Value"
                  onChange={(e) => {
                    const updated = [...localFilters];
                    updated[index].value = e.target.value;
                    // For text fields, label equals value - empty if no value
                    updated[index].label = e.target.value || "";
                    setLocalFilters(updated);
                  }}
                  sx={{ width: 170 }}
                />
              )}
              <IconButton
                size="small"
                onClick={() => {
                  setLocalFilters((prevFilters) => {
                    // Remove filter by ID if it exists, otherwise fall back to index
                    const updated = prevFilters.filter((f, i) => {
                      if (filter.id) {
                        return f.id !== filter.id;
                      }
                      return i !== index;
                    });
                    return updated;
                  });
                }}
              >
                <DeleteIcon color="error" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Divider />
      <Box
        sx={{
          p: 2,
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
        }}
      >
        <Button variant="contained" disableElevation onClick={handleAddFilter}>
          + Add
        </Button>

        <TextField
          select
          size="small"
          value={localFiltersLogic}
          onChange={(e) => {
            const newLogic = e.target.value;
            setLocalFiltersLogic(newLogic);
          }}
          label="Logic"
        >
          <MenuItem value="and">AND</MenuItem>
          <MenuItem value="or">OR</MenuItem>
        </TextField>

        {localFilters.length > 0 && (
          <Button variant="contained" color="error" disableElevation onClick={handleClearAllFilters}>
            Clear All
          </Button>
        )}
      </Box>
    </Popover>
  );
});

export default FiltersPopover;
